import { useEffect, useMemo, useState } from "react";

/**
 * Client-side pagination over an in-memory array. Used wherever the
 * upstream endpoint returns the full list and we want to paginate
 * locally (Marketing review queue, Admin dashboard users, etc.).
 *
 * Behaviour:
 * - Slices `items` into `paged` based on `page` × `perPage`.
 * - If `items` shrinks below the current offset (e.g. after a filter
 *   change), the hook auto-resets to page 1 so the user isn't
 *   stranded on a now-empty page.
 * - Changing `perPage` snaps the user back to page 1 — without this
 *   the experience is disorienting (e.g. you're on page 7 of 12 at
 *   10/page, change to 100/page, and end up on page 7 of 2).
 */
export function usePagedList<T>(
  items: T[],
  initialPerPage = 25,
): {
  paged: T[];
  page: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
  setPage: (p: number) => void;
  setPerPage: (n: number) => void;
} {
  const [page, setPageState] = useState(1);
  const [perPage, setPerPageState] = useState(initialPerPage);

  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(1, perPage)));

  // If the array shrinks below the current offset, slide back to a
  // page that actually has rows on it.
  useEffect(() => {
    if (page > totalPages) setPageState(totalPages);
  }, [page, totalPages]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return items.slice(start, start + perPage);
  }, [items, page, perPage]);

  const setPerPage = (n: number): void => {
    setPerPageState(n);
    setPageState(1);
  };

  return {
    paged,
    page,
    perPage,
    totalCount,
    totalPages,
    setPage: setPageState,
    setPerPage,
  };
}
